import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import '../styles/NPAT.scss';
import { useRole } from '../utils/RoleContext';
import { fmt, strip } from '../utils/numberFormat';
import { blurOnEnter } from '../utils/keyboard';


const buildFields = (prefix, endYear) => [
  { id: `${prefix} Current Year`,    label: endYear ? `Current Year (${endYear})`     : 'Current Year' },
  { id: `${prefix} Previous Year 1`, label: endYear ? `(${endYear - 1})` : 'Previous Year 1' },
  { id: `${prefix} Previous Year 2`, label: endYear ? `(${endYear - 2})` : 'Previous Year 2' },
  { id: `${prefix} Previous Year 3`, label: endYear ? `(${endYear - 3})` : 'Previous Year 3' },
  { id: `${prefix} Previous Year 4`, label: endYear ? `(${endYear - 4})` : 'Previous Year 4' },
];



const sectionSum = (fields, values) =>
  fields.reduce((sum, f) => {
    const n = parseFloat(strip(values[f.id] || '0'));
    return sum + (isNaN(n) ? 0 : n);
  }, 0);

const ManageNPAT = () => {
  const navigate = useNavigate();
  const { role } = useRole();
  const canEdit = role == 'Consultant';
  const [values, setValues] = useState({});
  const [saveStatus, setSaveStatus] = useState('idle'); // 'idle' | 'saving' | 'saved'
  const [errorModal, setErrorModal] = useState(null);
  const [financialYearEndYear, setFinancialYearEndYear] = useState(null);
  const [calc, setCalc] = useState(null);

  useEffect(() => {
    api.get('/core/finance-statutory/npat/')
      .then((res) => {
        if (res.data?.values) setValues(res.data.values);
        if (res.data?.financial_year_end_year) setFinancialYearEndYear(res.data.financial_year_end_year);
        if (res.data?.calculation) setCalc(res.data.calculation);

      })
      .catch((err) => console.error('Failed to load NPAT value:', err));
  }, []);

  const handleChange = (id, raw) => {
    setValues((prev) => ({ ...prev, [id]: raw }));
  };

  const handleBlur = (id) => {
    const raw = values[id] || '';
    if (!raw) return;
    const n = parseFloat(strip(raw));
    if (!isNaN(n)) setValues((prev) => ({ ...prev, [id]: fmt(n) }));
  };

  const handleFocus = (id) => {
    const val = values[id] || '';
    setValues((prev) => ({ ...prev, [id]: strip(val) }));
  };

  const currentRevenue = parseFloat(strip(values['Revenue Current Year'] || '0')) || 0;
  const currentNPAT = parseFloat(strip(values['NPAT Current Year'] || '0')) || 0;

  const handleSave = async () => {
    setSaveStatus('saving');
    try {
      const res = await api.post('/core/finance-statutory/npat/save/', { values, npat: currentNPAT, revenue: currentRevenue })
      if (res.data?.calculation) setCalc(res.data.calculation);
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (err) {
      setErrorModal('Failed to save. Please try again.');
      setSaveStatus('idle');
    }
  };

  const renderFields = (fields) =>
    fields.map((field) => (
      <div key={field.id} className="npat-field">
        <label className="npat-label" htmlFor={field.id}>
          {field.label}
        </label>
        <div className="npat-input-wrap">
          <span className="npat-currency">R</span>
          <input
            id={field.id}
            type="text"
            inputMode="decimal"
            className="npat-input"
            placeholder="0.00"
            value={values[field.id] || ''}
            onChange={(e) => handleChange(field.id, e.target.value)}
            onKeyDown={blurOnEnter}
            onBlur={() => handleBlur(field.id)}
            onFocus={() => handleFocus(field.id)}
            disabled={!canEdit}
          />
        </div>
      </div>
    ));

  return (
    <div className="npat-container">
      <div className="npat-header">
        <button className="npat-back" onClick={() => navigate('/finance-statutory')}>← Back</button>
        <div>
          <h1>Manage Revenue and NPAT</h1>
          <p>Define Revenue and NPAT for current and previous years</p>
        </div>
      </div>

      <div className="npat-body">

        {/* Revenue*/}
        <div className="npat-section-label npat-section-label--revenue">Revenue</div>
        <div className="npat-card npat-card--revenue">
          <div className="npat-card-top npat-card-top--green">
            <span><strong>Current Revenue and previous 5 years. Previous years Revenue to be taken from Audited Financial Statements</strong></span>
            <span className="npat-card-total">Current Year: R {fmt(currentRevenue)}</span>
          </div>
          <div className="npat-grid">
            {renderFields(buildFields('Revenue', financialYearEndYear))}
          </div>
        </div>

        {/* NPAT */}
        <div className="npat-section-label npat-section-label--npat">NPAT</div>
        <div className="npat-card npat-card--npat">
          <div className="npat-card-top npat-card-top--blue">
            <span><strong>Current NPAT and previous 5 years. If current NPAT not available, please use previous year's NPAT as a placeholder</strong></span>
            <span className="npat-card-total">Current Year: R {fmt(currentNPAT)}</span>
          </div>
          <div className="npat-grid">
            {renderFields(buildFields('NPAT', financialYearEndYear))}
          </div>
        </div>

        {/* Applicable NPAT Calculation */}
        <div className="npat-section-label npat-section-label--calc">Applicable NPAT Calculation</div>
        {!calc ? (
          <div className="npat-card npat-card--calc">
            <p className="npat-calc-empty">Save your values to see the applicable NPAT calculation.</p>
          </div>
        ) : calc.error ? (
          <div className="npat-card npat-card--calc">
            <p className="npat-calc-empty npat-calc-error">{calc.error}</p>
          </div>
        ) : (
          <div className="npat-card npat-card--calc">
            <div className="npat-card-top npat-card-top--violet">
              <span><strong>{calc.industry_label}</strong></span>
              <span className="npat-card-total">Applicable NPAT: R {fmt(calc.applicable_npat)}</span>
            </div>
            <div className="npat-table-wrap">
              <table className="npat-calc-table">
                <thead>
                  <tr>
                    <th>Year</th>
                    <th>Revenue</th>
                    <th>NPAT</th>
                    <th>Net Profit Margin</th>
                    <th>Industry Norm</th>
                  </tr>
                </thead>
                <tbody>
                  {calc.margins.map((row, i) => {
                    const label = buildFields('', financialYearEndYear)[i].label;
                    return (
                      <tr key={row.year} className={row.year === calc.qualifying_year ? 'qualifying' : ''}>
                        <td>{label}</td>
                        <td>R {fmt(row.revenue)}</td>
                        <td>R {fmt(row.npat)}</td>
                        <td>{(row.net_profit_margin * 100).toFixed(2)}%</td>
                        <td>{parseFloat((calc.threshold * 4 * 100).toFixed(4))}%</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <p className="npat-calc-note">
              {calc.qualifying_year
                ? `Qualifying year: ${calc.qualifying_year}`
                : 'No qualifying year — threshold applied to current revenue'}
            </p>
          </div>
        )}

        {/* Actions */}
        <div className="npat-actions">
          <button className="npat-btn npat-btn--primary" onClick={handleSave} disabled={saveStatus === 'saving' || !canEdit}>
            {saveStatus === 'saving' ? 'Saving...' : saveStatus === 'saved' ? 'Saved' : 'Save Values'}
          </button>
          <button className="npat-btn npat-btn--secondary" onClick={() => navigate('/finance-statutory')} disabled={saveStatus === 'saving'}>Cancel</button>
        </div>

      </div>

      {errorModal && (
        <div className="npat-modal-overlay">
          <div className="npat-modal">
            <h3 className="npat-modal-title">⚠ Save Failed</h3>
            <p className="npat-modal-body">{errorModal}</p>
            <div className="npat-modal-actions">
              <button className="npat-modal-btn npat-modal-btn--primary" onClick={() => setErrorModal(null)}>
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageNPAT;
